import React, { useEffect, useState } from "react";
import {
  Button,
  Layout,
  Row,
  Col,
  Typography,
  Tooltip,
  Modal,
  Table,
  Input,
  message,
  Spin,
  Breadcrumb,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/th";
import type { TableProps } from "antd";
import { useAuth } from "../../utils/Auth/AuthWrapper";
import { useNavigate } from "react-router-dom";
import "../taskCategory/styles/index.less";
import { get_all_category, category_delete } from "../../services/category";
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;
const { confirm } = Modal;

interface DataType {
  key: string;
  category_id: string;
  name: string;
  description: string;
  colortag: string;
  created_date: any;
}

type Props = {};

const TaskCategory = (props: Props) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [TaskCategoryData, setTaskCategoryData]: any = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  // set search
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // ✅ เริ่มต้นโหลดข้อมูล
      await getCategoryDetail();
      setIsLoading(false); // ✅ เมื่อโหลดเสร็จ ปิด loading
    };

    fetchData();
  }, []);

  useEffect(() => {
    const filtered = TaskCategoryData.filter((name: any) =>
      name.name.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchText, TaskCategoryData]);

  // get data
  const getCategoryDetail = async () => {
    try {
      const res = await get_all_category();
      const cleanedData = res.result.map((item: any) => ({
        ...item,
        description: item.description?.replace(/^"|"$/g, "") || "",
      }));
      setTaskCategoryData(cleanedData);
    } catch (e) {
      console.error("Error fetching category:", message || e);
      message.error("Failed to fetch category. Please try again later.");
    }
  };

  // search
  const onSearch = (value: any) => {
    setSearchText(value);
  };

  // api delete
  const showDeleteConfirm = (category_id: string, name: string) => {
    confirm({
      title: t("delete title"),
      icon: <ExclamationCircleFilled style={{ color: "#FF3B30" }} />,
      content: (
        <div
          className="custom-modal-content-news"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center", // จัดข้อความให้อยู่ตรงกลาง
          }}
        >
          <p>{t("delete detail category")} :</p>
          <p>
            <strong>{name}</strong>
          </p>
          <p>{t("yes or no")}</p>
        </div>
      ),
      okText: t("confirm"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          const res = await category_delete({ category_id: category_id });
          message.success("Delete Success!");
          getCategoryDetail();
        } catch (e) {
          console.error(e);
        }
      },
      onCancel() {},
    });
  };

  // table
  const columns: TableProps<DataType>["columns"] = [
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("name category")}
        </div>
      ),
      dataIndex: "name",
      key: "name",
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("detail category")}
        </div>
      ),
      dataIndex: "description",
      key: "description",
      width: 550,
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("create date")}
        </div>
      ),
      dataIndex: "created_date",
      key: "created_date",
      align: "center",
      render: (_: any, record: any) => {
        const formattedDate = dayjs(record.created_date)
          .locale("th")
          .format("D MMMM BBBB");

        return <div>{formattedDate}</div>;
      },
    },
    {
      title: "",
      key: "action",
      render: (_: any, record: DataType) => (
        <Row justify="center" align="middle">
          {auth.user.role === "Super Admin" && (
            <>
              <Tooltip placement="bottom" title="Edit">
                <Col
                  style={{
                    color: "#0958d9",
                    cursor: "pointer",
                    marginLeft: "10px",
                  }}
                  onClick={() => {
                    navigate("/task/category/edit/" + record.category_id);
                  }}
                >
                  <Button
                    style={{ backgroundColor: "#fff4cc" }}
                    icon={<EditOutlined style={{ color: "#FFB84C" }} />}
                  />
                </Col>
              </Tooltip>

              <Tooltip placement="bottom" title="Delete">
                <Col
                  style={{
                    color: "#0958d9",
                    cursor: "pointer",
                    marginLeft: "10px",
                  }}
                  onClick={() => {
                    if (
                      record.category_id ===
                      "99999999-0000-0000-0000-999999999999"
                    ) {
                      Modal.error({
                        title: "Permission Denied!",
                        content: `Not allowed to delete Super Admin's data.`,
                      });
                    } else {
                      showDeleteConfirm(record.category_id, record.name);
                    }
                  }}
                >
                  <Button
                    style={{ backgroundColor: "#FFDFDF" }}
                    icon={<DeleteOutlined style={{ color: "#B31312" }} />}
                  />
                </Col>
              </Tooltip>
            </>
          )}
        </Row>
      ),
    },
  ];

  return (
    <Layout>
      <Content className="category-head">
        {/* head */}
        <div>
          <Row justify="space-between" align="middle">
            <Breadcrumb
              separator=">"
              items={[
                {
                  title: (
                    <span style={{ fontWeight: "bold" }}>
                      {t("Task Category")}
                    </span>
                  ),
                },
              ]}
            />
            <Col>
              <Search
                placeholder="Search"
                onChange={(e) => onSearch(e.target.value)}
                onSearch={onSearch}
                style={{ width: 300 }}
              />
              {auth.user.role === "Super Admin" ? (
                <>
                  <Button
                    style={{
                      marginLeft: "10px",
                      backgroundColor: "#00215E",
                      color: "white",
                    }}
                    type="primary"
                    key="primary"
                    onClick={async () => {
                      navigate("/task/category/new");
                    }}
                  >
                    <PlusOutlined />
                    {t("Add Task Category")}
                  </Button>
                </>
              ) : null}
            </Col>
          </Row>
          <hr />
        </div>
      </Content>

      {/* table */}
      <Content className="category-table">
        {isLoading ? ( // ✅ ตรวจสอบสถานะ loading
          <Row justify="center" align="middle" style={{ height: "50vh" }}>
            <Spin size="large" />
          </Row>
        ) : (
          <Table
            rowKey="category_id"
            className="custom-table-news"
            columns={columns}
            dataSource={filteredData}
            pagination={{
              showTotal: (total, range) =>
                `แสดงรายการต่อหน้า : ${range[0]}-${range[1]} จาก ${total} รายการ`,
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
            scroll={{ x: "max-content" }}
          />
        )}
      </Content>
    </Layout>
  );
};

export default TaskCategory;
